import React, {
    useEffect,
} from "react";

import {
    Link,
    useNavigate,
} from "react-router-dom";

import {
    PiLockKeyDuotone,
    PiSparkleDuotone,
    PiCrownSimpleDuotone,
    PiCheckCircleDuotone,
    PiHouseDuotone,
} from "react-icons/pi";

import {
    useAuth,
} from "../context/AuthContext";

import "../assets/css/GuestExploreOverlay.css";
import logo from "../assets/images/Shaadilogo.png";


const GuestExploreOverlay = ({
    visible,

    clickCount =
        0,

    limit =
        10,

    verificationFailed =
        false,

    daily =
        false,

    pageLabel =
        "browsing",
}) => {

    const navigate =
        useNavigate();

    const {
        isAuthenticated,
    } = useAuth();


    /*
     * Logged-in users go to customer home.
     * Guests go to public landing page.
     */
    const homePath =
        isAuthenticated
            ? "/home"
            : "/";


    useEffect(() => {

        if (!visible) {
            return;
        }

        const previousOverflow =
            document
                .body
                .style
                .overflow;

        document
            .body
            .style
            .overflow =
            "hidden";


        return () => {

            document
                .body
                .style
                .overflow =
                previousOverflow;
        };

    }, [
        visible,
    ]);


    if (!visible) {
        return null;
    }


    const safeLimit =
        Number.isFinite(
            Number(limit)
        )
            ? Number(limit)
            : null;


    const safeCount =
        Math.max(
            0,
            Number(
                clickCount ||
                0
            )
        );


    const progress =
        safeLimit &&
        safeLimit > 0
            ? Math.min(
                100,
                Math.round(
                    (
                        safeCount /
                        safeLimit
                    ) *
                    100
                )
            )
            : 100;


    const title =
        verificationFailed
            ? "Access Verification Required"
            : daily
                ? "You’ve Reached Today’s Limit"
                : "Your Free Preview Is Complete";


    const description =
        verificationFailed
            ? (
                "We could not verify your browsing allowance. Sign in or choose a subscription to keep exploring."
            )
            : daily
                ? (
                    `You have reached today's ${pageLabel} browsing limit. Upgrade your plan to keep exploring without daily restrictions.`
                )
                : (
                    `You have reached the ${pageLabel} guest browsing limit. Unlock full access to continue discovering premium design assets.`
                );


    return (
        <div
            className="guest-explore-overlay"
            data-guest-explore-overlay="true"
            role="dialog"
            aria-modal="true"
            aria-labelledby="guest-explore-title"
        >

            <div
                className="guest-explore-backdrop-glow guest-explore-backdrop-glow-one"
            />

            <div
                className="guest-explore-backdrop-glow guest-explore-backdrop-glow-two"
            />


            <div
                className="guest-explore-card"
            >

                <div
                    className="guest-explore-card-shine"
                />


                {/* =================================================
                    CLICKABLE LOGO
                   ================================================= */}

                <Link
                    to={homePath}
                    className="guest-explore-logo"
                    aria-label="Go to home"
                >
                    <img
                        src={logo}
                        alt="Shaadi Material"
                    />
                </Link>


                {/* =================================================
                    LOCK VISUAL
                   ================================================= */}

                <div
                    className="guest-explore-lock-wrap"
                >

                    <div
                        className="guest-explore-lock-ring guest-explore-lock-ring-outer"
                    />

                    <div
                        className="guest-explore-lock-ring guest-explore-lock-ring-inner"
                    />

                    <div
                        className="guest-explore-lock-icon"
                    >
                        <PiLockKeyDuotone />
                    </div>


                    <span
                        className="guest-explore-sparkle guest-explore-sparkle-one"
                    >
                        <PiSparkleDuotone />
                    </span>


                    <span
                        className="guest-explore-sparkle guest-explore-sparkle-two"
                    >
                        <PiSparkleDuotone />
                    </span>

                </div>


                {/* =================================================
                    PREMIUM LABEL
                   ================================================= */}

                <div
                    className="guest-explore-eyebrow"
                >
                    <PiCrownSimpleDuotone />

                    <span>
                        Premium Access
                    </span>
                </div>


                <h2
                    id="guest-explore-title"
                >
                    {title}
                </h2>


                <p
                    className="guest-explore-copy"
                >
                    {description}
                </p>


                {/* =================================================
                    USAGE
                   ================================================= */}

                {!verificationFailed &&
                    safeLimit != null && (

                        <div
                            className="guest-explore-usage-card"
                        >

                            <div
                                className="guest-explore-usage-top"
                            >

                                <div>
                                    <span
                                        className="guest-explore-usage-label"
                                    >
                                        {
                                            daily
                                                ? "Today’s browsing"
                                                : "Free preview usage"
                                        }
                                    </span>
                                </div>


                                <div
                                    className="guest-explore-limit-chip"
                                >
                                    Limit reached
                                </div>

                            </div>


                            <div
                                className="guest-explore-progress-track"
                            >
                                <div
                                    className="guest-explore-progress-fill"
                                    style={{
                                        width:
                                            `${progress}%`,
                                    }}
                                />
                            </div>

                        </div>

                    )}


                {/* =================================================
                    BENEFITS
                   ================================================= */}

                <div
                    className="guest-explore-benefits"
                >

                    <div>
                        <PiCheckCircleDuotone />

                        <span>
                            Explore without browsing limits
                        </span>
                    </div>


                    <div>
                        <PiCheckCircleDuotone />

                        <span>
                            Access premium design assets
                        </span>
                    </div>


                    <div>
                        <PiCheckCircleDuotone />

                        <span>
                            Unlock subscriber-only benefits
                        </span>
                    </div>

                </div>


                {/* =================================================
                    ACTIONS
                   ================================================= */}

                <div
                    className="guest-explore-actions"
                >

                    <button
                        type="button"
                        className="guest-explore-primary"
                        onClick={() =>
                            navigate(
                                "/packages"
                            )
                        }
                    >

                        <span
                            className="guest-explore-primary-icon"
                        >
                            <PiCrownSimpleDuotone />
                        </span>

                        <span>
                            Buy Subscription
                        </span>

                        <span
                            className="guest-explore-button-arrow"
                        >
                            →
                        </span>

                    </button>


                    {/* LOGIN ONLY FOR GUEST */}

                    {!isAuthenticated && (

                        <button
                            type="button"
                            className="guest-explore-secondary"
                            onClick={() =>
                                navigate(
                                    "/login"
                                )
                            }
                        >
                            Already have an account?

                            <strong>
                                Log in
                            </strong>
                        </button>

                    )}


                    {/* HOME BUTTON */}

                    <button
                        type="button"
                        className="guest-explore-home"
                        onClick={() =>
                            navigate(
                                homePath
                            )
                        }
                    >
                        <PiHouseDuotone />

                        <span>
                            Back to Home
                        </span>
                    </button>

                </div>


                <div
                    className="guest-explore-footnote"
                >
                    Unlock your full Shaadi Material experience.
                </div>

            </div>

        </div>
    );
};


export default
    GuestExploreOverlay;